import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Trash2 } from 'lucide-react';
import { STATUS_COLOURS, fmt } from './constants';
import { SumCard, CatSumRow } from './StatCards';

const LotDetailDialog = ({ open, onOpenChange, lotDetail, isAdmin, onDelete }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 flex-wrap">
          <span>Lot {lotDetail?.lot_no}</span>
          {lotDetail && <Badge className={STATUS_COLOURS[lotDetail.status] || ''}>{lotDetail.status}</Badge>}
        </DialogTitle>
        <DialogDescription>Live summary of gold, stones, worker share and weight lost.</DialogDescription>
      </DialogHeader>
      {lotDetail && (
        <div className="overflow-y-auto flex-1 space-y-3 pr-1">
          <div className="text-xs text-slate-600">
            Progress: <b>{lotDetail.summary.pieces_broken}</b>/<b>{lotDetail.summary.pieces_total}</b>
            {lotDetail.summary.pieces_draft > 0 && <> · {lotDetail.summary.pieces_draft} draft</>}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs" data-testid="lot-summary-grid">
            <SumCard label="Gross received"     value={`${fmt(lotDetail.summary.gross_weight_total)} gm`} />
            <SumCard label="Gross broken"       value={`${fmt(lotDetail.summary.gross_weight_broken)} gm`} />
            <SumCard label="Shop Net (retained)" value={`${fmt(lotDetail.summary.current_net_total)} gm`} />
            <SumCard label="Worker Gold"        value={`${fmt(lotDetail.summary.worker_gold_total)} gm`} />
            <SumCard label="Net Gold (total)"   value={`${fmt(lotDetail.summary.net_gold_total)} gm`} />
            <SumCard label="Fine Gold (total)"  value={`${fmt(lotDetail.summary.fine_gold_total)} gm`} highlight />
            <SumCard label="Fine Gold (shop)"   value={`${fmt(lotDetail.summary.fine_gold_shop_total)} gm`} highlight />
            <SumCard label="Stones (all)"       value={`${fmt(lotDetail.summary.stone_weight_total)} gm`} />
            <SumCard label="Weight Lost"        value={`${fmt(lotDetail.summary.weight_lost)} gm`} danger />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <CatSumRow label="Diamonds"     data={lotDetail.summary.diamonds} />
            <CatSumRow label="Stones"       data={lotDetail.summary.stones} />
            <CatSumRow label="Beads/Pearls" data={lotDetail.summary.beads_pearls} />
          </div>

          <div className="border rounded-lg divide-y">
            {lotDetail.pieces.map((p) => (
              <div key={p._id} className="p-2.5 text-xs flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono">{p.serial_no}</div>
                  <div className="text-slate-500">
                    Gross {fmt(p.gross_weight)} · Purity {fmt(p.expected_purity, 2)}%
                    {p.breakage && <> · Net {fmt(p.breakage.net_gold_wt || 0)} gm</>}
                  </div>
                </div>
                <Badge className={STATUS_COLOURS[p.status] || ''}>{p.status}</Badge>
                {p.breakage_draft && <span className="text-yellow-700 text-[10px]">(draft)</span>}
              </div>
            ))}
          </div>

          {/* Admin: Delete Lot (only when open + no breakage anywhere) */}
          {isAdmin && lotDetail.status === 'open'
            && lotDetail.summary.pieces_broken === 0
            && lotDetail.summary.pieces_draft === 0 && (
            <Button
              variant="destructive"
              className="w-full h-11"
              onClick={() => onDelete(lotDetail)}
              data-testid={`lot-delete-${lotDetail.lot_no}`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete Lot &amp; revert pieces
            </Button>
          )}
          {isAdmin && lotDetail.status === 'open'
            && (lotDetail.summary.pieces_broken > 0 || lotDetail.summary.pieces_draft > 0) && (
            <div className="text-[11px] text-slate-500 text-center">
              Cannot delete — some pieces already have breakage or drafts.
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default LotDetailDialog;
